import { ATTENDANCE_BAND_LABELS, NAMED_ABSENCE_REASONS, type AbsenceReason, type AttendanceBand, type AttendanceState, type PollAnswerInput, type PollDetailDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ApiError, api, isNetworkError } from '../lib/api';
import { ABSENCE_REASON_LABELS, formatDateTime, formatShort } from '../lib/format';
import { ATTENDANCE_BAND_STYLE, formatDay, today } from '../lib/sports';
import { Badge, Button, Card, CardBody, EmptyState, Field, Input, LoadingBlock, Select, cn, useToast } from '../components/ui';
import { PollAnswerForm, PollResults } from '../components/polls/PollAnswerForm';
import { PushCard } from '../components/PushCard';
import { PushPrompt } from '../components/PushPrompt';
import { isIos, isStandalone } from '../lib/push';

interface PersonalAbsence { id: string; reason: AbsenceReason; fromDate: string; toDate: string; note: string | null; editable: boolean }

interface PersonalOverview {
  club: string; team: string; teams?: string[]; person: string; expiresAt: string;
  absences: PersonalAbsence[];
  teilnahme: { band: AttendanceBand; gewertet: number; entschuldigt: number; verlauf: { date: string; state: AttendanceState }[] } | null;
  sessions: Array<{
    absence: { reason: AbsenceReason; fromDate: string; toDate: string } | null;
    guestOf: string | null;
    team?: string;
    venue?: 'HOME' | 'AWAY' | null;
    id: string; kind: 'TRAINING' | 'MATCH' | 'WORK'; title: string; startAt: string; endAt: string;
    location: string; cancelled: boolean; allowMaybe: boolean; locked: boolean;
    /** Hinweis der Mannschaftsleitung, etwa Treffpunkt oder Absagegrund. */
    description?: string;
    /** Für diesen Termin freigegeben: wer hat zu- und abgesagt. */
    responses?: { name: string; rsvp: 'YES' | 'NO' | 'MAYBE' | 'PENDING' }[] | null;
    responseDeadlineAt: string | null;
    response: { rsvp: 'YES' | 'NO' | 'MAYBE' | 'PENDING'; reason: AbsenceReason; note: string | null; version: number; isLate: boolean; auto?: boolean };
  }>;
}

/** Persönliche Rückmeldung ohne Konto. Das URL-Fragment wird nie an den Server geschickt. */
export function TrainingInvitePage(): JSX.Element {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
  const [alleTermine, setAlleTermine] = useState(false);
  const queryKey = ['personal-sports-access'];
  useLinkManifest();
  // Der Einstiegscode wird beim Laden aus der Adresse genommen und dort entfernt.
  // Scheitert die erste Abfrage am Netz, braucht "Nochmal versuchen" ihn weiterhin.
  const start = useRef<{ hash: string; appCode: string | null } | null>(null);
  // Aus Erinnerung oder Push: dieser Termin soll gleich sichtbar sein.
  const ziel = useRef<string | null | undefined>(undefined);
  if (ziel.current === undefined) ziel.current = new URLSearchParams(window.location.search).get('termin');
  if (start.current === null) {
    const hash = window.location.hash.slice(1);
    start.current = { hash, appCode: new URLSearchParams(window.location.search).get('app') ?? (hash.startsWith('app=') ? hash.slice(4) : null) };
  }
  const overview = useQuery({
    queryKey,
    queryFn: async () => {
      const { hash, appCode } = start.current!;
      const geladen = await laden(hash, appCode);
      start.current = { hash: '', appCode: null };
      return geladen;
    },
    retry: false,
  });
  async function laden(hash: string, appCode: string | null): Promise<PersonalOverview> {
    if (appCode) {
      // Startadresse der App auf dem Home-Bildschirm (siehe InstallButton).
      // Dort sofort einloesen. Im Browser gilt dessen eigener Zugang - fehlt
      // der, kommt die Seite aus einem anderen Browser (etwa aus WhatsApp
      // "In Safari oeffnen"), und der Code meldet diesen Browser an.
      window.history.replaceState(null, '', window.location.pathname);
      const adopt = () => api.post<PersonalOverview>('/api/public/sports-access/adopt', { token: appCode });
      if (isStandalone()) {
        try {
          return await adopt();
        } catch {
          // Schon eingeloest: die App hat inzwischen ihren eigenen Zugang.
        }
        return api.get<PersonalOverview>('/api/public/sports-access/me');
      }
      try {
        return await api.get<PersonalOverview>('/api/public/sports-access/me');
      } catch {
        return adopt();
      }
    }
    const token = hash;
    if (token) {
      const data = await api.post<PersonalOverview>('/api/public/sports-access/exchange', { token });
      window.history.replaceState(null, '', window.location.pathname);
      return data;
    }
    return api.get<PersonalOverview>('/api/public/sports-access/me');
  }
  useHomeScreenCode(Boolean(overview.data));

  const zielTermin = ziel.current;
  const zielDaten = overview.data;
  useEffect(() => {
    if (!zielTermin || !zielDaten) return;
    const index = zielDaten.sessions.findIndex((session) => session.id === zielTermin);
    if (index < 0) return;
    const grenze = Date.now() + 7 * 86_400_000;
    if (index > 0 && Date.parse(zielDaten.sessions[index]!.startAt) > grenze) setAlleTermine(true);
    ziel.current = null;
    if (window.location.search) window.history.replaceState(null, '', window.location.pathname);
    window.requestAnimationFrame(() => {
      const element = document.getElementById(`termin-${zielTermin}`);
      element?.scrollIntoView({ block: 'center' });
      element?.classList.add('ring-2', 'ring-brand-400', 'ring-inset');
      window.setTimeout(() => element?.classList.remove('ring-2', 'ring-brand-400', 'ring-inset'), 4000);
    });
  }, [zielTermin, zielDaten]);

  // Beim Zurueckkehren in die App stimmt der Stand oft nicht mehr.
  const nachladen = overview.refetch;
  const geladen = Boolean(overview.data);
  useEffect(() => {
    if (!geladen) return;
    const beiSichtbar = (): void => { if (document.visibilityState === 'visible') void nachladen(); };
    document.addEventListener('visibilitychange', beiSichtbar);
    return () => document.removeEventListener('visibilitychange', beiSichtbar);
  }, [geladen, nachladen]);
  const respond = useMutation({
    mutationFn: async (input: { sessionId: string; rsvp: 'YES' | 'NO' | 'MAYBE'; reason?: AbsenceReason; note?: string | null; version: number }) => {
      const senden = (version: number) =>
        api.put(`/api/public/sports-access/sessions/${input.sessionId}/response`, { rsvp: input.rsvp, reason: input.reason, note: input.note, version });
      try {
        return await senden(input.version);
      } catch (error) {
        if (!istVersionskonflikt(error)) throw error;
        // Jemand anderes (oder ein zweites Geraet) war schneller: mit dem neuen Stand genau einmal wiederholen.
        const neu = (await overview.refetch()).data?.sessions.find((session) => session.id === input.sessionId);
        if (!neu || neu.locked || neu.cancelled) throw error;
        return senden(neu.response.version);
      }
    },
    onSuccess: async (_result, input) => {
      await queryClient.invalidateQueries({ queryKey });
      if (input.note !== undefined) {
        setNoteDrafts((vorher) => { const naechste = { ...vorher }; delete naechste[input.sessionId]; return naechste; });
        toast.show('Notiz gespeichert');
        return;
      }
      toast.show(input.rsvp === 'YES' ? 'Zusage gespeichert' : input.rsvp === 'MAYBE' ? 'Vielleicht gespeichert' : 'Absage gespeichert');
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey });
        toast.show(istVersionskonflikt(error) ? 'Das hat sich gerade geändert – bitte nochmal tippen' : error.message, 'error');
        return;
      }
      toast.show(error instanceof ApiError ? error.message : 'Konnte nicht gespeichert werden', 'error');
    },
  });

  if (overview.isLoading) return <LoadingBlock label="Persönlichen Zugang prüfen …" />;
  if (overview.error && fehlerArt(overview.error) === 'verbindung') {
    return (
      <Frame>
        <EmptyState
          title="Keine Verbindung – dein Link ist in Ordnung"
          description="Der Server ist gerade nicht erreichbar. Versuch es gleich noch einmal."
          action={<Button loading={overview.isFetching} onClick={() => void overview.refetch()}>Nochmal versuchen</Button>}
        />
      </Frame>
    );
  }
  if (overview.error && fehlerArt(overview.error) === 'zuviele') {
    return (
      <Frame>
        <EmptyState
          title="Zu viele Versuche"
          description="Bitte in ein paar Minuten nochmal."
          action={<Button variant="secondary" loading={overview.isFetching} onClick={() => void overview.refetch()}>Nochmal versuchen</Button>}
        />
      </Frame>
    );
  }
  if (overview.error || !overview.data) {
    return (
      <Frame>
        <EmptyState
          title="Dieser persönliche Link gilt nicht mehr"
          description={[
            // Der Server sagt, warum: Gerät abgemeldet, Link ersetzt oder abgelaufen.
            overview.error instanceof ApiError && overview.error.status === 403 ? overview.error.message : null,
            isStandalone() ? 'Öffne deinen persönlichen Link noch einmal in Safari und lege die Seite von dort erneut auf den Home-Bildschirm.' : null,
          ].filter(Boolean).join(' ') || undefined}
        />
        <LinkAnfordern />
      </Frame>
    );
  }
  const data = overview.data;
  const offen = data.sessions.filter(istOffen).length;
  // Alles, was noch zu beantworten waere, ist zugesagt oder automatisch eingeplant.
  const aktive = data.sessions.filter((session) => !session.locked && !session.cancelled && !session.absence);
  const nurEingeplant = aktive.some((session) => session.response.auto) && aktive.every((session) => session.response.auto || session.response.rsvp === 'YES');
  // Zuerst nur die naechsten 7 Tage - mindestens aber der naechste Termin.
  const grenze = Date.now() + 7 * 86_400_000;
  const naechste = data.sessions.filter((session, index) => index === 0 || Date.parse(session.startAt) <= grenze);
  const sichtbar = alleTermine ? data.sessions : naechste;
  const weitere = data.sessions.length - naechste.length;
  // Offene Rueckmeldungen spaeter nicht verstecken, ohne es zu sagen.
  const weitereOffen = data.sessions.slice(naechste.length).filter(istOffen).length;
  return (
    <Frame club={data.club} team={data.team} person={data.person}>
      <PushPrompt channel="link" />

      {data.sessions.length > 0 && (
        <p
          className={cn(
            'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium',
            offen > 0 ? 'bg-amber-100 text-amber-900' : 'bg-emerald-100 text-emerald-800',
          )}
        >
          <span aria-hidden="true">{offen > 0 ? '●' : '✓'}</span>
          {offen === 0 ? (nurEingeplant ? 'Du bist für alle Termine eingeplant – wenn du nicht kannst, sag ab.' : 'Alles beantwortet') : offen === 1 ? '1 Rückmeldung offen' : `${offen} Rückmeldungen offen`}
        </p>
      )}

      <EigeneUmfragen />

      <section aria-labelledby="termine" className="space-y-3">
        <h2 id="termine" className="text-xs font-semibold uppercase tracking-wide text-slate-500">Deine Termine</h2>
        {data.sessions.length === 0 && <EmptyState title="Kein Termin geplant" description="Neue Trainings und Spiele erscheinen automatisch hier." />}
        {sichtbar.length > 0 && (
          <div className="card divide-y divide-slate-100 overflow-hidden">
            {nachTagen(sichtbar).map(([tag, termine]) => (
              <div key={tag}>
                <h3 className="bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">{tagTitel(tag)}</h3>
                <ul className="divide-y divide-slate-100">
                  {termine.map((session) => (
                    <li key={session.id} id={`termin-${session.id}`} data-termin className="transition-shadow">
                      <TerminZeile
                        session={session}
                        mitMannschaft={(data.teams?.length ?? 1) > 1}
                        busy={respond.isPending}
                        entwurf={noteDrafts[session.id]}
                        onEntwurf={(wert) => setNoteDrafts({ ...noteDrafts, [session.id]: wert })}
                        onRespond={(input) => respond.mutate({ sessionId: session.id, ...input })}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        {weitere > 0 && (
          <Button variant="secondary" className="w-full" onClick={() => setAlleTermine(!alleTermine)}>
            {alleTermine
              ? 'Nur die nächsten 7 Tage'
              : `${weitere} weitere ${weitere === 1 ? 'Termin' : 'Termine'} anzeigen${weitereOffen > 0 ? ` (${weitereOffen} offen)` : ''}`}
          </Button>
        )}
      </section>

      {/* Nebensaechliches unten und kleiner - zuerst kommt, was eine Antwort braucht. */}
      <section aria-labelledby="mehr" className="space-y-3 pt-2">
        <h2 id="mehr" className="text-xs font-semibold uppercase tracking-wide text-slate-500">Mehr</h2>
        <EigeneAbwesenheiten absences={data.absences ?? []} onChanged={() => queryClient.invalidateQueries({ queryKey })} />
        {data.teilnahme && <EigeneTeilnahme teilnahme={data.teilnahme} />}
        <PushCard channel="link" compact />
      </section>
    </Frame>
  );
}

type Session = PersonalOverview['sessions'][number];
type Antwort = { rsvp: 'YES' | 'NO' | 'MAYBE'; reason?: AbsenceReason; note?: string | null; version: number };

/** Netz weg oder Serverfehler: der Link selbst ist nicht schuld. */
function fehlerArt(error: unknown): 'verbindung' | 'zuviele' | 'ungueltig' {
  if (isNetworkError(error)) return 'verbindung';
  if (!(error instanceof ApiError)) return 'verbindung';
  if (error.status >= 500) return 'verbindung';
  if (error.status === 429) return 'zuviele';
  return 'ungueltig';
}

function istVersionskonflikt(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && /inzwischen geändert/.test(error.message);
}

/** Braucht noch eine Antwort - und kann sie auch noch bekommen. */
function istOffen(session: Session): boolean {
  return !session.locked && !session.cancelled && !session.absence
    && session.response.rsvp === 'PENDING' && !session.response.auto;
}


const WAHL: Record<Antwort['rsvp'], { label: string; zeichen: string; aktiv: string; status: string; farbe: string }> = {
  YES: { label: 'Zusagen', zeichen: '✓ Komme', aktiv: 'bg-emerald-600 text-white', status: 'Zugesagt', farbe: 'text-emerald-700' },
  MAYBE: { label: 'Vielleicht', zeichen: '? Vielleicht', aktiv: 'bg-amber-500 text-white', status: 'Vielleicht', farbe: 'text-amber-700' },
  NO: { label: 'Absagen', zeichen: '✕ Nicht dabei', aktiv: 'bg-rose-600 text-white', status: 'Abgesagt', farbe: 'text-rose-700' },
};

/** Kalendertag im Browser - Termine eines Tages stehen zusammen. */
function tagVon(value: string | number): string {
  const d = new Date(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function nachTagen(sessions: Session[]): [string, Session[]][] {
  const tage = new Map<string, Session[]>();
  for (const session of sessions) {
    const tag = tagVon(session.startAt);
    tage.set(tag, [...(tage.get(tag) ?? []), session]);
  }
  return [...tage];
}

function tagTitel(tag: string): string {
  const [jahr, monat, tagImMonat] = tag.split('-').map(Number);
  const datum = new Date(jahr!, monat! - 1, tagImMonat!);
  const text = datum.toLocaleDateString('de-AT', { weekday: 'short', day: 'numeric', month: 'short' }).replace(/\./g, '').replace(',', '');
  if (tag === tagVon(Date.now())) return `Heute · ${text}`;
  if (tag === tagVon(Date.now() + 86_400_000)) return `Morgen · ${text}`;
  return text;
}

/** Farbstreifen und Vorlesetext je Terminart. */
const ART_TEXT: Record<'TRAINING' | 'MATCH' | 'WORK', string> = { TRAINING: 'Training', MATCH: 'Spiel', WORK: 'Arbeitseinsatz' };

function uhrzeit(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Ein Termin als Zeile: Uhrzeit und Titel, darunter Ort, Mannschaft und
 * Stand. Die Antwort sind kleine Schalter rechts - die gewaehlte Seite ist
 * eingefaerbt. Offene Termine sind hinterlegt; Farbstreifen: Spiel blau,
 * Training gruen. Grund und Notiz bleiben eingeklappt, bis jemand sie will.
 */
function TerminZeile({
  session,
  mitMannschaft,
  busy,
  entwurf,
  onEntwurf,
  onRespond,
}: {
  session: Session;
  mitMannschaft: boolean;
  busy: boolean;
  entwurf: string | undefined;
  onEntwurf: (wert: string) => void;
  onRespond: (input: Antwort) => void;
}): JSX.Element {
  const offen = istOffen(session);
  const { rsvp, auto, version } = session.response;
  const optionen: Antwort['rsvp'][] = session.allowMaybe ? ['YES', 'MAYBE', 'NO'] : ['YES', 'NO'];
  const notiz = entwurf ?? session.response.note ?? '';
  const [details, setDetails] = useState(false);
  const speichern = (): void => {
    const wert = notiz.trim();
    if (wert === (session.response.note ?? '')) return;
    onRespond({ rsvp: rsvp as Antwort['rsvp'], note: wert || null, version });
  };
  const grund = session.response.reason !== 'NONE' ? ABSENCE_REASON_LABELS[session.response.reason] : null;
  const zusammenfassung = [grund, session.response.note].filter(Boolean).join(' · ');

  const fristVorbei = session.responseDeadlineAt !== null && Date.parse(session.responseDeadlineAt) < Date.now();
  // Auch nach der Antwort bleibt sichtbar, bis wann sie sich noch ohne Vermerk aendern laesst.
  const beantwortet = rsvp !== 'PENDING' && !auto;
  const fristZeile = session.locked || session.cancelled || !session.responseDeadlineAt || !(beantwortet || auto)
    ? null
    : fristVorbei
      ? 'Frist vorbei – Änderungen gelten als verspätet'
      : auto ? null : `Ändern bis ${formatShort(session.responseDeadlineAt)}`;

  const stand = session.cancelled
    ? <span className="font-medium text-rose-700">Entfällt</span>
    : rsvp !== 'PENDING' && !auto
      ? <span className={cn('font-medium', WAHL[rsvp].farbe)}>{WAHL[rsvp].status}</span>
      : auto
        ? <span className="font-medium text-emerald-700">
          Eingeplant
          {!session.locked && !fristVorbei && (session.responseDeadlineAt ? ` · absagen bis ${formatShort(session.responseDeadlineAt)}` : ' · du kannst absagen')}
        </span>
        : session.absence
          ? <span className="font-medium text-slate-600">Abwesend</span>
          : offen && session.responseDeadlineAt
            ? <span className="font-medium text-amber-800">Antwort bis {formatShort(session.responseDeadlineAt)}</span>
            : <span className="font-medium text-amber-800">Offen</span>;
  // Mannschaft und Heim/Auswaerts vorne und nie abgeschnitten - das
  // Vorspiel der 1b und das KM-Spiel tragen oft denselben Titel.
  const mannschaft = session.guestOf ? `bei ${session.guestOf}` : mitMannschaft ? session.team : null;

  return (
    <div className={cn('flex gap-2.5 px-3 py-2.5', offen && 'bg-amber-50', session.locked && !session.cancelled && 'opacity-70')}>
      <span
        className={cn('w-1 shrink-0 self-stretch rounded-full', session.kind === 'MATCH' ? 'bg-brand-500' : session.kind === 'WORK' ? 'bg-amber-500' : 'bg-emerald-500')}
        title={ART_TEXT[session.kind]}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className={cn('truncate font-medium leading-snug text-slate-900', session.cancelled && 'text-slate-500 line-through')}>
              <span className="tabular-nums">{uhrzeit(session.startAt)}</span> {session.title}
              <span className="sr-only"> ({ART_TEXT[session.kind]})</span>
            </p>
            <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-slate-500">
              {mannschaft && (
                <span className={cn('shrink-0 rounded px-1.5 py-px font-medium', session.guestOf ? 'bg-sky-100 text-sky-800' : 'bg-slate-100 text-slate-700')}>
                  {mannschaft}
                </span>
              )}
              {session.venue && (
                <span className={cn('shrink-0 rounded px-1.5 py-px font-medium', session.venue === 'HOME' ? 'bg-emerald-50 text-emerald-800' : 'bg-violet-50 text-violet-800')}>
                  {session.venue === 'HOME' ? 'Heim' : 'Auswärts'}
                </span>
              )}
              <span className="min-w-0 break-words">
                {stand}
                {session.response.isLate && <span className="text-amber-700"> · nach Frist</span>}
                {session.location ? ` · ${session.location}` : ''}
              </span>
            </p>
            {fristZeile && <p className={cn('text-xs', fristVorbei ? 'text-amber-800' : 'text-slate-500')}>{fristZeile}</p>}
            {session.description && (
              <p className={cn('mt-1 whitespace-pre-line break-words text-xs', session.cancelled ? 'font-medium text-rose-800' : 'text-slate-600')}>
                {session.description}
              </p>
            )}
          </div>
        </div>

        {!session.locked && (
          <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label={`Rückmeldung ${session.title}`}>
            {optionen.map((option) => {
              const gewaehlt = rsvp === option && !(option === 'YES' && auto);
              const eingeplant = option === 'YES' && auto;
              return (
                <button
                  key={option}
                  type="button"
                  aria-label={WAHL[option].label}
                  title={WAHL[option].label}
                  aria-pressed={gewaehlt}
                  disabled={busy}
                  onClick={() => {
                    // Eine automatische Zusage bestaetigt man mit "Zusagen" ausdruecklich.
                    if (!gewaehlt) onRespond({ rsvp: option, version });
                  }}
                  className={cn(
                    'flex min-h-[44px] flex-1 items-center justify-center rounded-lg px-3 py-2 text-center text-sm font-semibold ring-1 ring-inset ring-slate-300 transition-colors disabled:opacity-60',
                    gewaehlt ? WAHL[option].aktiv : eingeplant ? 'bg-white text-emerald-700' : 'bg-white text-slate-600 hover:bg-slate-50',
                  )}
                >
                  <span aria-hidden="true">{WAHL[option].zeichen}</span>
                </button>
              );
            })}
          </div>
        )}

        {session.responses && session.responses.length > 0 && <Rueckmeldungen liste={session.responses} />}

        {session.absence && !session.cancelled && (rsvp === 'PENDING' || rsvp === 'NO') && (
          <p className="mt-1 text-xs text-amber-800">
            Abwesend {formatDay(session.absence.fromDate)} – {formatDay(session.absence.toDate)} ({ABSENCE_REASON_LABELS[session.absence.reason]})
            {!session.locked && ' – kommst du doch, einfach zusagen.'}
          </p>
        )}

        {!session.locked && (rsvp === 'NO' || rsvp === 'MAYBE') && !details && (
          <button type="button" className="mt-0.5 inline-flex min-h-[44px] items-center py-2 text-left text-xs text-brand-700 hover:underline" onClick={() => setDetails(true)}>
            {zusammenfassung
              ? <><span className="text-slate-600">{zusammenfassung}</span> · ändern</>
              : rsvp === 'NO' ? '+ Grund oder Notiz' : '+ Notiz'}
          </button>
        )}

        {!session.locked && (rsvp === 'NO' || rsvp === 'MAYBE') && details && (
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            {rsvp === 'NO' && (
              <Field label="Grund" hint="Freiwillig">{(id) => <Select id={id} value={session.response.reason} onChange={(event) => onRespond({ rsvp: 'NO', reason: event.target.value as AbsenceReason, version })}><option value="NONE">Kein Grund angegeben</option>{NAMED_ABSENCE_REASONS.map((value) => <option key={value} value={value}>{ABSENCE_REASON_LABELS[value]}</option>)}</Select>}</Field>
            )}
            <div>
              <Field label="Notiz" hint={rsvp === 'MAYBE' ? 'z. B. „komme später“ oder „entscheide am Vortag“' : 'Freiwillig'}>
                {(id) => (
                <Input
                  id={id}
                  value={notiz}
                  maxLength={200}
                  placeholder={rsvp === 'MAYBE' ? 'Warum vielleicht?' : 'z. B. voraussichtlich 2 Wochen'}
                  onChange={(event) => onEntwurf(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Enter') (event.target as HTMLInputElement).blur(); }}
                  onBlur={speichern}
                />
              )}
            </Field>
              <Button
                size="sm"
                variant="secondary"
                className="mt-2"
                disabled={busy || notiz.trim() === (session.response.note ?? '')}
                // Mouse-down soll das Feld nicht verlassen - sonst speichert schon das Verlassen, und der Klick sendet doppelt.
                onMouseDown={(event) => event.preventDefault()}
                onClick={speichern}
              >
                Speichern
              </Button>
            </div>
          </div>
        )}

        {session.locked && !session.cancelled && (
          <p className="mt-0.5 text-xs text-slate-500">
            Hat begonnen – Änderungen über die Mannschaftsleitung.
            {session.response.note ? ` Notiz: ${session.response.note}` : ''}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Umfragen an die Person - auch ohne Zugang beantwortbar.
 *
 * Erst nach dem Einloesen des Links geladen: vorher gibt es kein Geraete-Cookie.
 */
function EigeneUmfragen(): JSX.Element | null {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [bearbeiten, setBearbeiten] = useState<string | null>(null);
  const polls = useQuery({
    queryKey: ['personal-polls'],
    queryFn: () => api.get<PollDetailDto[]>('/api/public/sports-access/polls'),
    retry: false,
  });
  const antworten = useMutation({
    mutationFn: (input: { id: string; answer: PollAnswerInput }) => api.put(`/api/public/sports-access/polls/${input.id}/answer`, input.answer),
    onSuccess: async () => {
      setBearbeiten(null);
      await queryClient.invalidateQueries({ queryKey: ['personal-polls'] });
      toast.show('Danke, deine Antwort ist gespeichert');
    },
    onError: (error: unknown) => toast.show(error instanceof ApiError ? error.message : 'Konnte nicht gespeichert werden', 'error'),
  });
  if (!polls.data || polls.data.length === 0) return null;
  return (
    <>
      {polls.data.map((poll) => {
        const formular = poll.isOpen && (!poll.myAnsweredAt || bearbeiten === poll.id);
        return (
          <Card key={poll.id}>
            <CardBody className="space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">{poll.title}</p>
                  <p className="text-sm text-slate-500">
                    Umfrage · {poll.organisationUnitName}
                    {poll.isOpen && poll.closesAt ? ` · bis ${formatDateTime(poll.closesAt)}` : ''}
                  </p>
                </div>
                <div className="flex gap-1">
                  {poll.anonymous && <Badge tone="info">Anonym</Badge>}
                  {!poll.isOpen ? <Badge>Beendet</Badge> : poll.myAnsweredAt ? <Badge tone="success">Beantwortet</Badge> : <Badge tone="warning">Antwort offen</Badge>}
                </div>
              </div>
              {poll.description && <p className="whitespace-pre-wrap text-sm text-slate-700">{poll.description}</p>}
              {formular ? (
                <PollAnswerForm
                  poll={poll}
                  submitting={antworten.isPending}
                  onSubmit={(answer) => antworten.mutate({ id: poll.id, answer })}
                  onCancel={bearbeiten === poll.id ? () => setBearbeiten(null) : undefined}
                />
              ) : (
                <>
                  {poll.myAnsweredAt && <p className="text-sm text-slate-600">Deine Antwort ist gespeichert.</p>}
                  {poll.isOpen && poll.myAnswers && (
                    <Button size="sm" variant="secondary" onClick={() => setBearbeiten(poll.id)}>Antwort ändern</Button>
                  )}
                  <PollResults poll={poll} />
                </>
              )}
            </CardBody>
          </Card>
        );
      })}
    </>
  );
}

/**
 * Selbsthilfe statt Nachfrage bei der Mannschaftsleitung: Wer sich selbst
 * angemeldet hat, bekommt seinen Link an dieselbe Adresse noch einmal. Die
 * Antwort ist immer gleich - sie verraet nicht, welche Adressen es gibt.
 */
const gueltig = (adresse: string): boolean => /^\S+@\S+\.\S+$/.test(adresse.trim());

function LinkAnfordern(): JSX.Element {
  const toast = useToast();
  const [adresse, setAdresse] = useState('');
  const anfordern = useMutation({
    mutationFn: () => api.post<{ ok: boolean; mailEnabled?: boolean }>('/api/public/sports-access/resend', { email: adresse.trim() }),
    onSuccess: (result) => {
      setAdresse('');
      if (result.mailEnabled === false) {
        toast.show('Der Verein verschickt gerade keine E-Mails – bitte die Mannschaftsleitung um einen neuen Link', 'error');
        return;
      }
      toast.show('Falls die Adresse bekannt ist, kommt der Link in wenigen Minuten per E-Mail');
    },
    onError: (error: unknown) => toast.show(error instanceof ApiError ? error.message : 'Konnte nicht angefordert werden', 'error'),
  });

  return (
    <Card>
      <CardBody className="space-y-3">
        <div>
          <p className="font-semibold">Link verloren?</p>
          <p className="text-sm text-slate-500">
            Wenn du dich selbst angemeldet hast, schicken wir ihn noch einmal an deine E-Mail-Adresse.
            Der alte Link gilt dann nicht mehr.
          </p>
        </div>
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(event) => { event.preventDefault(); if (gueltig(adresse)) anfordern.mutate(); }}
        >
          <Input
            type="email"
            aria-label="E-Mail-Adresse"
            className="flex-1"
            placeholder="name@example.at"
            value={adresse}
            onChange={(event) => setAdresse(event.target.value)}
          />
          <Button type="submit" loading={anfordern.isPending} disabled={!gueltig(adresse)}>Link schicken</Button>
        </form>
        <p className="hint">Sonst hilft die Mannschaftsleitung mit einem neuen Link weiter.</p>
      </CardBody>
    </Card>
  );
}

/**
 * Auf dem iPhone legt man die Seite ueber "Teilen" ab, ohne dass die Seite
 * davon erfaehrt. Deshalb steht dort immer schon ein frischer, einmaliger
 * Code in der Adresse - Safari uebernimmt sie als Startadresse der App, und
 * die App meldet sich damit beim ersten Start an (siehe Migration 044).
 */
function useHomeScreenCode(ready: boolean): void {
  useEffect(() => {
    if (!ready || !isIos() || isStandalone()) return;
    void api.post<{ code: string }>('/api/public/sports-access/transfer')
      .then(({ code }) => window.history.replaceState(null, '', `/antworten?app=${code}`))
      .catch(() => undefined);
  }, [ready]);
}

/**
 * Eigenes Manifest fuer diese Seite: abgelegt auf dem Startbildschirm soll die
 * App hier starten und nicht bei der Anmeldung fuer Zugaenge. Der Server
 * liefert es fuer /antworten schon im HTML aus; das hier deckt den Wechsel
 * innerhalb der Anwendung ab.
 */

function useLinkManifest(): void {
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
    if (!link) return;
    const previous = link.getAttribute('href');
    link.setAttribute('href', '/manifest-antworten.webmanifest');
    return () => { if (previous) link.setAttribute('href', previous); };
  }, []);
}

/** Eigene längere Abwesenheiten melden - etwa den Sommerurlaub am Stück statt jedes Training einzeln. */
/**
 * Eigene Teilnahme als Band - ohne Prozentzahl und ohne Vergleich.
 *
 * Entschuldigtes Fehlen zaehlt nicht in die Wertung, sondern steht grau
 * daneben. Die Punktreihe zeigt die letzten acht Wochen.
 */
function EigeneTeilnahme({ teilnahme }: { teilnahme: NonNullable<PersonalOverview['teilnahme']> }): JSX.Element {
  const punkt: Record<AttendanceState, { farbe: string; text: string }> = {
    PRESENT: { farbe: 'bg-emerald-500', text: 'dabei' },
    EXCUSED: { farbe: 'bg-slate-300', text: 'entschuldigt' },
    ABSENT: { farbe: 'bg-rose-500', text: 'gefehlt' },
    UNKNOWN: { farbe: 'bg-slate-200', text: 'nicht erfasst' },
  };
  return (
    <Card>
      <CardBody className="space-y-2">
        <p className="text-sm font-medium text-slate-700">Deine Teilnahme an Trainings</p>
        <p className={`inline-block rounded-lg px-3 py-1.5 text-sm font-semibold ${ATTENDANCE_BAND_STYLE[teilnahme.band].feld}`}>
          {ATTENDANCE_BAND_LABELS[teilnahme.band]}
        </p>
        {teilnahme.verlauf.length > 0 && (
          <div
            className="flex flex-wrap gap-1"
            role="img"
            aria-label={`Letzte Wochen: ${teilnahme.verlauf.map((termin) => punkt[termin.state].text).join(', ')}`}
          >
            {teilnahme.verlauf.map((termin) => (
              <span
                key={termin.date}
                title={`${formatDay(termin.date)}: ${punkt[termin.state].text}`}
                className={`h-2.5 w-2.5 rounded-full ${punkt[termin.state].farbe}`}
              />
            ))}
          </div>
        )}
        {teilnahme.entschuldigt > 0 && (
          <p className="text-xs text-slate-500">
            {teilnahme.entschuldigt} {teilnahme.entschuldigt === 1 ? 'Termin' : 'Termine'} wegen Verletzung, Krankheit oder
            eingetragener Abwesenheit – nicht gewertet.
          </p>
        )}
        {teilnahme.band === 'UNKNOWN' && (
          <p className="text-xs text-slate-500">Noch zu wenige gewertete Trainings für eine Einschätzung.</p>
        )}
        <p className="text-xs text-slate-400">Nur für dich sichtbar. Gezählt werden Trainings mit bestätigter Anwesenheit.</p>
      </CardBody>
    </Card>
  );
}

function EigeneAbwesenheiten({ absences, onChanged }: { absences: PersonalAbsence[]; onChanged: () => Promise<unknown> }): JSX.Element {
  const toast = useToast();
  const [offen, setOffen] = useState(false);
  const [reason, setReason] = useState<AbsenceReason>('VACATION');
  const [fromDate, setFromDate] = useState(() => today());
  const [toDate, setToDate] = useState(() => today(7));
  const [note, setNote] = useState('');
  const fehler = (error: unknown) => toast.show(error instanceof ApiError ? error.message : 'Konnte nicht gespeichert werden', 'error');

  const anlegen = useMutation({
    mutationFn: async () => {
      // Schon gegebene Zusagen im Zeitraum gelten sonst weiter - deshalb fragen.
      const konflikte = await api.get<{ responses: number }>(`/api/public/sports-access/absences/conflicts?fromDate=${fromDate}&toDate=${toDate}`);
      const cancelResponses = konflikte.responses > 0 && window.confirm(
        konflikte.responses === 1
          ? 'Du hast für einen Termin in diesem Zeitraum schon zugesagt. Soll daraus eine Absage werden?'
          : `Du hast für ${konflikte.responses} Termine in diesem Zeitraum schon zugesagt. Sollen daraus Absagen werden?`,
      );
      return api.post<{ id: string; cancelledResponses?: number }>('/api/public/sports-access/absences', { reason, fromDate, toDate, note: note.trim() || null, cancelResponses });
    },
    onSuccess: async (result) => {
      await onChanged();
      setOffen(false);
      setNote('');
      const abgesagt = result.cancelledResponses ?? 0;
      toast.show(abgesagt > 0 ? `Abwesenheit eingetragen – ${abgesagt === 1 ? '1 Zusage' : `${abgesagt} Zusagen`} abgesagt` : 'Abwesenheit eingetragen');
    },
    onError: fehler,
  });
  const loeschen = useMutation({
    mutationFn: (id: string) => api.del(`/api/public/sports-access/absences/${id}`),
    onSuccess: async () => {
      await onChanged();
      toast.show('Abwesenheit gelöscht');
    },
    onError: fehler,
  });

  return (
    <Card><CardBody className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-semibold">Abwesenheiten</p>
          <p className="text-sm text-slate-500">Urlaub oder Verletzung? Für alle Termine im Zeitraum bist du dann abgemeldet.</p>
        </div>
        {!offen && <Button size="sm" variant="secondary" onClick={() => setOffen(true)}>Abwesenheit melden</Button>}
      </div>

      {absences.length > 0 && (
        <ul className="divide-y divide-slate-100 text-sm">
          {absences.map((absence) => (
            <li key={absence.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span>
                <span className="font-medium">{formatDay(absence.fromDate)} – {formatDay(absence.toDate)}</span>
                <span className="text-slate-600"> · {ABSENCE_REASON_LABELS[absence.reason]}{absence.note ? ` · ${absence.note}` : ''}</span>
                {!absence.editable && <span className="block text-xs text-slate-500">von der Mannschaftsleitung eingetragen</span>}
              </span>
              {absence.editable && (
                <button className="-my-1 inline-flex min-h-[44px] items-center px-2 text-rose-700 hover:underline" disabled={loeschen.isPending} onClick={() => { if (window.confirm('Diese Abwesenheit löschen?')) loeschen.mutate(absence.id); }}>Löschen</button>
              )}
            </li>
          ))}
        </ul>
      )}

      {offen && (
        <div className="space-y-3 rounded-xl border border-slate-200 p-3">
          <Field label="Grund" required>
            {(id) => <Select id={id} value={reason} onChange={(event) => setReason(event.target.value as AbsenceReason)}>{NAMED_ABSENCE_REASONS.map((value) => <option key={value} value={value}>{ABSENCE_REASON_LABELS[value]}</option>)}</Select>}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Von" required>{(id) => <Input id={id} type="date" min={today()} value={fromDate} onChange={(event) => { setFromDate(event.target.value); if (event.target.value > toDate) setToDate(event.target.value); }} />}</Field>
            <Field label="Bis (ca.)" required>{(id) => <Input id={id} type="date" min={fromDate} value={toDate} onChange={(event) => setToDate(event.target.value)} />}</Field>
          </div>
          <Field label="Notiz" hint="Freiwillig">{(id) => <Input id={id} value={note} maxLength={200} onChange={(event) => setNote(event.target.value)} placeholder="z. B. Familienurlaub" />}</Field>
          <div className="flex gap-2">
            <Button size="sm" loading={anlegen.isPending} disabled={!fromDate || !toDate || toDate < fromDate} onClick={() => anlegen.mutate()}>Eintragen</Button>
            <Button size="sm" variant="ghost" onClick={() => setOffen(false)}>Abbrechen</Button>
          </div>
        </div>
      )}
    </CardBody></Card>
  );
}

function Frame({ club, team, person, children }: { club?: string; team?: string; person?: string; children: React.ReactNode }): JSX.Element {
  return <div className="mx-auto min-h-screen w-full max-w-2xl px-4 py-6 sm:py-10"><header className="mb-4"><p className="text-xs uppercase tracking-wide text-slate-400">{club || 'UK Info'}</p><h1 className="page-title">{team ? `${team}: Rückmeldung` : 'Persönliche Rückmeldung'}</h1><p className="page-subtitle">{person ? `Für ${person} · ohne Registrierung` : 'Ohne Registrierung'}</p></header><div className="space-y-4">{children}</div></div>;
}

/**
 * Wer kommt? Fuer freigegebene Termine, aufklappbar.
 *
 * Nur Namen und die Rueckmeldung - warum jemand fehlt, steht hier bewusst
 * nicht. Eingeklappt, damit die Liste der Termine kurz bleibt.
 */
function Rueckmeldungen({ liste }: { liste: { name: string; rsvp: 'YES' | 'NO' | 'MAYBE' | 'PENDING' }[] }): JSX.Element {
  const gruppen = [
    { rsvp: 'YES' as const, titel: 'Zugesagt', farbe: 'text-emerald-700' },
    { rsvp: 'MAYBE' as const, titel: 'Vielleicht', farbe: 'text-sky-700' },
    { rsvp: 'NO' as const, titel: 'Abgesagt', farbe: 'text-amber-700' },
    { rsvp: 'PENDING' as const, titel: 'Offen', farbe: 'text-slate-500' },
  ].map((gruppe) => ({ ...gruppe, namen: liste.filter((eintrag) => eintrag.rsvp === gruppe.rsvp).map((eintrag) => eintrag.name) }))
    .filter((gruppe) => gruppe.namen.length > 0);
  const zusagen = liste.filter((eintrag) => eintrag.rsvp === 'YES').length;

  return (
    <details className="mt-1.5 text-xs">
      <summary className="cursor-pointer text-slate-500">
        Wer kommt? {zusagen} von {liste.length} zugesagt
      </summary>
      <div className="mt-1 space-y-1">
        {gruppen.map((gruppe) => (
          <p key={gruppe.rsvp} className="text-slate-600">
            <span className={cn('font-medium', gruppe.farbe)}>{gruppe.titel} ({gruppe.namen.length}):</span>{' '}
            {gruppe.namen.join(', ')}
          </p>
        ))}
      </div>
    </details>
  );
}
